import { ISalesLine } from '@msdyn365-commerce-modules/order-management';
import { CartLine, SalesLine, SimpleProduct, SalesOrder } from '@msdyn365-commerce/retail-proxy';
import { ICartlinesViewProps } from '../../modules/ext-cart/components/ext-cart-line-items';
import { formatPrice } from '../../utils/helper';
import { ProductRecommendationItem } from './hooks/useProductRecommendations';
import { RelewiseLineItem, RelewiseCart, RelewiseOrder } from './RelewiseTypes';
import { RelewiseProductResult } from './types';

const DEFAULT_RELEWISE_ENVIRONMENT = 'test-next';

const DIMENSION_TYPE_VALUES_MAP = {
    1: 'color',
    3: 'size'
};
interface VariantDimensions {
    color?: string;
    size?: string;
}
interface LineItemInput {
    product: SimpleProduct | undefined;
    lineData: CartLine | SalesLine | undefined;
}

class RelewiseMappingService {
    public prefixId(itemId: string): string;
    public prefixId(itemId: string | undefined): string | undefined;
    public prefixId(itemId: string | undefined) {
        if (!itemId) {
            return;
        }
        const envName = window.___initialData___?.requestContext.app.config.environmentName;
        if (!envName) {
            console.warn(`Environment name is not set up, Relewise requests will fall back to ${DEFAULT_RELEWISE_ENVIRONMENT}`);
        }
        return `${envName ?? DEFAULT_RELEWISE_ENVIRONMENT}|${itemId}`;
    }

    public mapProductDimensionsToRelewiseVariantId(itemId: string, color: string, size: string) {
        const normalizedColor = color.replace(/ /g, '_').replace(/#/g, '');
        const normalizedSize = size.replace(/ /g, '_').replace(/\//g, '');

        return `${this.prefixId(itemId)}${normalizedColor}${normalizedSize}`;
    }

    public mapToRelewiseCart(subtotalAmount: number, cartLines: ICartlinesViewProps[] | undefined, currency: string): RelewiseCart {
        const lineItems =
            cartLines?.map(({ data }) =>
                this.mapToRelewiseLineItem({
                    lineData: data?.cartline,
                    product: data?.product
                })
            ) ?? [];

        return {
            subtotal: { amount: subtotalAmount, currency },
            lineItems
        };
    }

    public mapToRelewiseOrder(salesLines: ISalesLine[], salesOrder: SalesOrder): RelewiseOrder {
        const lineItems =
            salesLines.map(({ data }) =>
                this.mapToRelewiseLineItem({
                    lineData: data?.salesLine,
                    product: data?.product
                })
            ) ?? [];

        return {
            subtotal: { amount: salesOrder.SubtotalAmount ?? 0, currency: salesOrder.CurrencyCode ?? '' },
            lineItems,
            orderNumber: salesOrder.Id
        };
    }

    private mapToRelewiseLineItem({ lineData, product }: LineItemInput): RelewiseLineItem {
        const variantId = this.mapSimpleProductToRelewiseVariantId(product);
        return {
            lineTotal: lineData?.TotalAmount ?? 0,
            productId: this.prefixId(lineData?.ItemId) ?? '',
            quantity: lineData?.Quantity ?? 0,
            ...(variantId ? { variantId } : {})
        };
    }

    public mapSimpleProductToRelewiseVariantId(product: SimpleProduct | undefined): string | undefined {
        if (!product?.ItemId) {
            return;
        }

        const variantDimensions = product.Dimensions?.reduce<VariantDimensions>((acc, val) => {
            const dimensionName = DIMENSION_TYPE_VALUES_MAP[val.DimensionTypeValue];
            if (dimensionName) {
                acc[dimensionName] = val.DimensionValue?.Value;
            }
            return acc;
        }, {});

        const { color, size } = variantDimensions ?? {};
        if (!color || !size) {
            return;
        }

        return this.mapProductDimensionsToRelewiseVariantId(product.ItemId, color, size);
    }

    public mapRelewiseProductResultListToRecommendationItems(products: RelewiseProductResult[] | undefined): ProductRecommendationItem[] {
        if (!products) {
            return [];
        }
        return products.map(this.mapRelewiseProductResultToRecommendationItem);
    }

    public mapRelewiseProductResultToRecommendationItem(product: RelewiseProductResult): ProductRecommendationItem {
        const { locale, channel } = window.___initialData___?.requestContext ?? {};
        const basePriceFormatted = formatPrice(product.variant?.listPrice?.toString(), channel?.Currency, locale);
        const finalPriceFormatted = formatPrice(product.variant?.salesPrice?.toString(), channel?.Currency, locale);
        return {
            id: product.variant?.variantId ?? '',
            displayName: product.variant?.displayName ?? '',
            imgUrl: product.variant?.data.PrimaryImageUrl.value ?? '',
            basePriceFormatted,
            finalPriceFormatted,
            hasDiscount: product.variant?.listPrice !== product.variant?.salesPrice,
            productPageUrl: product.variant?.data.Url.value ?? ''
        };
    }
}

export const relewiseMappingService = new RelewiseMappingService();
